import React from 'react';
import { useNavigate } from 'react-router-dom';
import { C, F, IMG } from '../theme';

const SERVICES = [
  {
    img: `${IMG}/care-handhold.png`, tag: 'For aging parents', title: 'Elderly Care',
    body: 'Dependable, dignified daily support for parents who deserve to age with comfort and respect. Because our caregivers are nurse-trained, they know how to spot when something is changing.',
    points: ['Bathing & dressing', 'Meal preparation', 'Mobility support', 'Medication reminders', 'Companionship', 'Personalised care plan'],
  },
  {
    img: `${IMG}/care-child2.png`, tag: 'For children', title: 'Special Needs Care',
    body: 'Patient, qualified, consistent care built around each child — working closely with the family toward a shared routine and clear goals, with everything well documented.',
    points: ['Consistent qualified care', 'Built around the child', 'Working with the family', 'Clear shared goals', 'Well-documented care', 'Personalised care plan'],
  },
];
const ESSENTIALS = ['Bathing & dressing', 'Meal preparation', 'Companionship', 'Mobility support', 'Medication reminders', 'Watching for changes'];
const STEPS = [
  { n: '1', title: 'Tell us what you need', body: 'Through our form, you describe who needs care, the kind of help required, and your location and preferences. It takes only a few minutes.' },
  { n: '2', title: 'We match you with a verified caregiver', body: "We propose a fully vetted, nurse-trained caregiver who fits your loved one's needs. You decide who feels right, there's no obligation until you do." },
  { n: '3', title: 'Care begins, with a clear plan', body: 'Your caregiver works to a personalised care plan and the daily essentials are handled. After each engagement you can rate and review, so standards keep rising.' },
];

export default function Services() {
  const navigate = useNavigate();

  return (
    <div className="cn-fade">
      {/* HERO */}
      <section style={{ background: 'linear-gradient(180deg,#F4EFE4 0%,#FBF7F0 100%)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '88px 24px 60px', textAlign: 'center' }}>
          <p style={{ textTransform: 'uppercase', letterSpacing: '.14em', fontSize: 13, fontWeight: 800, color: C.orange, margin: '0 0 18px' }}>Our services</p>
          <h1 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 54, lineHeight: 1.06, letterSpacing: '-.015em', color: C.tealDeep, margin: '0 0 20px' }}>Trusted, nurse-trained care for the people you love</h1>
          <p style={{ fontSize: 20, lineHeight: 1.6, color: C.body, margin: 0 }}>Our caregivers are registered nurses and trained caregivers with real experience — helping with daily essentials and knowing how to spot when something is changing.</p>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 24px 40px', display: 'flex', flexDirection: 'column', gap: 40 }}>
        {SERVICES.map((sv) => (
          <div key={sv.title} data-cn-service-row style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, background: '#fff', border: '1px solid #E9E2D4', borderRadius: 24, overflow: 'hidden', boxShadow: '0 30px 56px -40px rgba(21,58,51,.45)' }}>
            <img src={sv.img} alt={sv.title} data-cn-service-img style={{ width: '100%', height: '100%', minHeight: 340, objectFit: 'cover' }} />
            <div style={{ padding: '44px 46px' }}>
              <span style={{ display: 'inline-block', background: C.sageTint, color: C.teal, fontWeight: 800, fontSize: 12.5, letterSpacing: '.05em', textTransform: 'uppercase', padding: '6px 12px', borderRadius: 999, marginBottom: 18 }}>{sv.tag}</span>
              <h2 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 32, color: '#173B34', margin: '0 0 14px' }}>{sv.title}</h2>
              <p style={{ fontSize: 16, lineHeight: 1.66, color: '#54635D', margin: '0 0 24px' }}>{sv.body}</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {sv.points.map((p) => (
                  <div key={p} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, color: '#3A4842', fontWeight: 600 }}>
                    <span style={{ flexShrink: 0, color: C.orange, fontWeight: 900 }}>✓</span>{p}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* DAILY ESSENTIALS BAND */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '56px 24px' }}>
        <div data-cn-essentials-band style={{ background: C.tealDeep, borderRadius: 24, padding: '56px 48px', color: C.sageTint }}>
          <div data-cn-essentials-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 44, alignItems: 'center' }}>
            <div>
              <h2 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 34, lineHeight: 1.14, color: '#fff', margin: '0 0 14px' }}>The daily essentials, handled with warmth</h2>
              <p style={{ fontSize: 16.5, lineHeight: 1.66, color: '#C4D6CF', margin: 0 }}>Because our caregivers are nurse-trained, they help with the everyday while watching for the small changes that matter, and every person has a personalised care plan so families and caregivers work toward the same goals.</p>
            </div>
            <div data-cn-essentials-chips style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {ESSENTIALS.map((e) => (
                <div key={e} style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 14, padding: '16px 18px', fontSize: 15, fontWeight: 700, color: '#fff', display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ color: C.orangeSoft, fontWeight: 900 }}>✓</span>{e}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '70px 24px 40px' }}>
        <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 52px' }}>
          <p style={{ textTransform: 'uppercase', letterSpacing: '.14em', fontSize: 13, fontWeight: 800, color: C.orange, margin: '0 0 14px' }}>Getting started</p>
          <h2 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 42, lineHeight: 1.1, color: C.tealDeep, margin: 0 }}>From first contact to confident care</h2>
        </div>
        <div data-cn-steps-grid2 style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 26 }}>
          {STEPS.map((s) => (
            <div key={s.n} style={{ background: '#fff', border: '1px solid #EDE6D8', borderRadius: 20, padding: '34px 30px' }}>
              <div style={{ width: 54, height: 54, borderRadius: 16, background: C.teal, color: '#fff', fontFamily: F.serif, fontSize: 26, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 22 }}>{s.n}</div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: '0 0 10px' }}>{s.title}</h3>
              <p style={{ fontSize: 15.5, lineHeight: 1.62, color: '#5A6963', margin: 0 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 24px 96px' }}>
        <div style={{ background: 'linear-gradient(120deg,#F6E9DB 0%,#EAF2EE 100%)', borderRadius: 28, padding: '64px 40px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 42, lineHeight: 1.08, color: C.tealDeep, margin: '0 0 16px' }}>Let's find the right caregiver</h2>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: C.body, margin: '0 auto 30px', maxWidth: 500 }}>Tell us who needs care and we'll match you with a fully vetted, nurse-trained caregiver.</p>
          <button className="cn-primary-btn" onClick={() => navigate('/contact')} style={{ background: C.orange, color: '#fff', border: 'none', fontFamily: 'inherit', fontSize: 18, fontWeight: 800, padding: '17px 38px', borderRadius: 999, cursor: 'pointer', boxShadow: '0 16px 34px -14px rgba(224,124,69,.8)' }}>Find a caregiver</button>
        </div>
      </section>
    </div>
  );
}
